import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { RunEventRow } from '../lib/fleetStream';

// The BODY of one timeline node. Three shapes, in order of how much a reader
// gets out of them:
//
//   1. `run_provenance` / `usage` / `usage_ledger` — first-class FACTS. "Which model wrote this
//      and what did it cost" is already answerable from the PR body this run
//      opened (`apps/worker` renders the provenance chain into it) and was NOT
//      answerable on the platform's own run page, where both shipped as raw JSON
//      inside a collapsed <details>. 736 prod runs carry provenance, 199 usage.
//   2. an ABSENT payload — reported as absent. `payload_json` is `{}` for a null
//      column, which reads exactly like a box that recorded an empty object, and
//      2,963 of 5,170 prod `tool_call` payloads are already null because the
//      72-hour raw-log janitor took them.
//   3. everything else — the raw payload, still collapsed, so first paint is not
//      blown by a large body.
//
// The parsers are LENIENT by design (the `parseHygieneSummaryEvent` rule): they
// pick the fields this view renders and tolerate everything else, so a payload
// shape the box changes never breaks the timeline.

/** One `label: value` line, rendered only when the payload actually carries it. */
type Fact = { readonly label: string; readonly value: string };

function field(payload: unknown, key: string): string | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const v = (payload as Record<string, unknown>)[key];
  if (typeof v === 'string' && v.trim() !== '') return v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}

function parsePayload(ev: RunEventRow): unknown {
  try {
    return JSON.parse(ev.payload_json);
  } catch {
    return null;
  }
}

/**
 * `[payload field, label]` per kind this view renders as facts, ordered
 * most-answering-first: the model is what a reader came for.
 *
 * The labels are STATIC `t('…')` calls on purpose. They were a table of key
 * strings handed to `t(labelKey)`, which `i18n.coverage.test.ts` cannot read, and
 * none of the eight keys existed: the timeline shipped `⟦fleet.usage_input_tokens⟧`
 * beside the right number. Written as literals, the coverage guard gates them.
 */
function factSpec(kind: string): readonly (readonly [string, string])[] | null {
  switch (kind) {
    case 'run_provenance':
      return [
        ['model', t('fleet.provenance_model')],
        ['provider', t('fleet.provenance_provider')],
        ['harness', t('fleet.provenance_harness')],
        ['reason', t('fleet.provenance_reason')],
      ];
    case 'usage':
      return [
        ['model', t('fleet.provenance_model')],
        ['input_tokens', t('fleet.usage_input_tokens')],
        ['output_tokens', t('fleet.usage_output_tokens')],
        ['total_tokens', t('fleet.usage_total_tokens')],
        ['steps', t('fleet.usage_steps')],
      ];
    // The box's attestation that its `usage` rows are the run's whole spend
    // (`RunUsageLedgerSchema`): unequal counts mean a pass went unmetered.
    case 'usage_ledger':
      return [
        ['passes', t('fleet.usage_ledger_passes')],
        ['usage_rows', t('fleet.usage_ledger_usage_rows')],
      ];
    default:
      return null;
  }
}

/** The facts a `run_provenance` / `usage` / `usage_ledger` row carries, or `null` for every other kind. */
export function runEventFacts(ev: RunEventRow): readonly Fact[] | null {
  const spec = factSpec(ev.kind);
  if (spec === null) return null;
  const payload = parsePayload(ev);
  const facts: Fact[] = [];
  for (const [key, label] of spec) {
    const value = field(payload, key);
    if (value !== null) facts.push({ label, value });
  }
  return facts.length > 0 ? facts : null;
}

export type RunEventFactsProps = {
  readonly event: RunEventRow;
};

export function RunEventFacts(props: RunEventFactsProps): JSX.Element {
  const facts = (): readonly Fact[] | null => runEventFacts(props.event);
  return (
    <Show
      when={facts()}
      fallback={
        <Show
          when={props.event.payload_present}
          fallback={<p class="timeline__meta">{t('fleet.payload_absent')}</p>}
        >
          <details class="fleet-timeline__payload">
            <summary class="audit-timeline__label">{t('fleet.payload')}</summary>
            <pre class="code-block">{props.event.payload_json}</pre>
          </details>
        </Show>
      }
    >
      {(rows) => (
        <dl class="timeline__body">
          <For each={rows()}>
            {(fact) => (
              <>
                <dt class="audit-timeline__label">{fact.label}</dt>
                <dd class="timeline__meta">{fact.value}</dd>
              </>
            )}
          </For>
        </dl>
      )}
    </Show>
  );
}
